/* eslint-disable no-param-reassign */
import { MutationTree } from 'vuex';
import { Location } from '@/interfaces/Location';
import { IRetailer } from '@/interfaces/Retailer';
import { IShoppingListProduct } from '@/interfaces/ShoppingList';
import { Tab } from '@/interfaces/Tab';
import { IMxrConfig } from '@/interfaces/MxrConfig';
import { ProductShoppingListSection } from '@/components/tabs/components/product-shopping-list/ProductShoppingList';
import RetailerOffer from '@/interfaces/RetailerOffer';
import MxrRetailer from '@/interfaces/MxrRetailer';
import { Takeover } from '@/takeover';
import { PromotionCarouselItem } from '@/interfaces/PromotionCarouselItem';
import { ProductShoppingListItem } from '@/interfaces/ProductShoppingListItem';
import {
	AuthOnBehalfOfConfig,
	GetLocationsResponse,
	GetLocationsResponseKr,
} from '@andromeda/polaris-data-access-products';
import {
	CLOSE_SWAP_MODAL,
	NEXT_CARD,
	OPEN_MXR_SWAP_MODAL,
	PREV_CARD,
	SELECT_CARD,
	SELECT_TAB,
	SET_ACTIVE_CARD,
	SET_BETA,
	SET_CAROUSEL_PROMOTIONS,
	SET_CURRENT_MXR_RETAILER_INDEX,
	SET_FULFILLMENT_STORE,
	SET_GO_TO_CART_OPEN,
	SET_IS_DEFAULT,
	SET_KROGER_AUTH_CONFIG,
	SET_LOADING_PROMOTIONS,
	SET_MERCURY_LOCATION_RESULT,
	SET_MXR_CONFIG,
	SET_MXR_JSON_DATA,
	SET_MXR_RETAILER_LOCATIONS,
	SET_MXR_RETAILER_OFFERS,
	SET_PROMOTION_CAROUSELS,
	SET_RETAILER,
	SET_SELECTED_SWAP,
	SET_TAKEOVER,
	SetMxrRetailerOffersPayload,
	TOGGLE_RETAILER_LOCATIONS_MODAL,
	UPDATE_PRODUCT_SHOPPING_LIST_ITEM,
	UPDATE_PRODUCT_SHOPPING_LIST_SECTIONS,
	UPDATE_SWAP_OPTIONS,
	UPDATE_VISIBLE_INGREDIENTS,
	UPDATE_VISIBLE_PROMOTION_IDS,
	SET_CHECKOUT_TOTAL,
	SET_SELECTED_TAB_INDEX,
	SET_PREVIOUS_PROMOTION,
SET_OFFERS_COUNT_IN_CART,
} from '../types';
import { IRootState } from './state';

let isFirstConfigSet = false;

export const mutations: MutationTree<IRootState> = {
	[NEXT_CARD](state: IRootState) {
		state.previousCardIndex = state.activeCardIndex;
		let nextIndex = state.activeCardIndex + 1;
		if (state.config.cards.length === 0) {
			nextIndex = -1;
		} else if (nextIndex >= state.config.cards.length) {
			nextIndex = 0;
		}
		state.activeCardIndex = nextIndex;
	},
	[PREV_CARD](state: IRootState) {
		state.previousCardIndex = state.activeCardIndex;
		let nextIndex = state.activeCardIndex - 1;
		if (nextIndex < 0) {
			nextIndex = state.config.cards.length - 1;
		}
		state.activeCardIndex = nextIndex;
	},
	[SELECT_TAB](state: IRootState, tab: Tab) {
		state.selectedTab = tab;
	},
	[SET_SELECTED_TAB_INDEX](state: IRootState, tab: number) {
		state.selectedTabIndex = tab;
	},
	[SELECT_CARD](state: IRootState, card: number) {
		state.activeCardIndex = card;
	},
	[UPDATE_VISIBLE_PROMOTION_IDS](
		state: IRootState,
		{ carouselSlot, incomingId, uniqueId }
	) {
		state.visiblePromotionIds.splice(
			carouselSlot,
			1,
			incomingId.toString()
		);
		state.visiblePromotionUniqueIds.splice(
			carouselSlot,
			1,
			uniqueId.toString()
		);
	},
	[SET_PROMOTION_CAROUSELS](
		state: IRootState,
		carousels: PromotionCarouselItem[]
	) {
		if (state.config.promotions.length) {
			state.config.promotions = [];
		}
		for (let i = 0; i < carousels.length; i += 1) {
			state.config.promotions.push(carousels[i]);
			if (carousels[i].promotions.length) {
				state.visiblePromotionIds.splice(
					i,
					1,
					carousels[i].promotions[0].id.toString()
				);
				state.visiblePromotionUniqueIds.splice(
					i,
					1,
					carousels[i].promotions[0].uniqueId.toString()
				);
			}
		}
	},
	[SET_LOADING_PROMOTIONS](state: IRootState, loading: boolean) {
		state.loadingPromotions = loading;
	},
	[SET_MXR_RETAILER_LOCATIONS](state: IRootState, location: Location) {
		state.config.retailer.location = location;
	},
	[SET_RETAILER](state: IRootState, retailer: IRetailer) {
		state.config.retailer = retailer;
	},
	[SET_MXR_JSON_DATA](state: IRootState, json: string) {
		state.mxrJSONData = json;
	},
	[SET_MERCURY_LOCATION_RESULT]: (
		state: IRootState,
		locationResult:
			| GetLocationsResponse
			| GetLocationsResponseKr
			| Error
			| null
	) => {
		state.mercuryLocationResult = locationResult;
	},
	[SET_FULFILLMENT_STORE]: (
		state: IRootState,
		fulfillmentStore: GetLocationsResponse
	) => {
		state.fulfillmentStore = fulfillmentStore;
	},
	[SET_MXR_RETAILER_LOCATIONS]: (
		state: IRootState,
		mxrRetailers: MxrRetailer[]
	) => {
		state.mxrRetailerLocations = mxrRetailers;
	},
	[SET_CURRENT_MXR_RETAILER_INDEX]: (state: IRootState, index: number) => {
		state.currentMxrRetailerIndex = index;
	},
	[SET_MXR_RETAILER_OFFERS]: (
		state: IRootState,
		payload: SetMxrRetailerOffersPayload
	) => {
		const offers = payload.offers as RetailerOffer[];
		state.mxrRetailerLocations
			.filter((r) => r.FulfillmentStoreId === payload.fulfillmentStoreId)
			.forEach((r) => {
				r.SetOffers(offers);
				r.SetPromotions(state.config.promotions);
				// Set Shopping List Sections
				for (let i = 0; i < state.config.cards.length; i += 1) {
					const card = state.config.cards[i];
					for (let x = 0; x < card.shoppingList.length; x += 1) {
						const sl = card.shoppingList[x];
						for (let y = 0; y < sl.items.length; y += 1) {
							const item = sl.items[y];
							const filteredOffers = item.ingredientId
								? r.Offers.filter(
										(o) =>
											item.ingredientId.toString() ===
											o.ItemId.toString()
								  )
								: r.Offers.filter(
										(o) =>
											item.id.toString() ===
											o.ItemId.toString()
								  );
							let offer = RetailerOffer.CreateDefault();
							if (filteredOffers.length) {
								[offer] = filteredOffers;
							}
							if (offer.Name.length) {
								item.product = {
									id: offer.Id,
									name: offer.Name,
									price: offer.Price,
									image: offer.Image,
									itemId: offer.ItemId,
									upc: offer.Upc,
								} as IShoppingListProduct;
								sl.items[y] = item;
							}
						}
					}
				}
			});
	},
	[UPDATE_PRODUCT_SHOPPING_LIST_ITEM]: (
		state: IRootState,
		item: ProductShoppingListItem
	) => {
		for (let i = 0; i < state.productShoppingListSections.length; i += 1) {
			const section = state.productShoppingListSections[i];
			for (let y = 0; y < section.Items.length; y += 1) {
				const secItem = section.Items[y];
				if (secItem.UniqueId === item.UniqueId) {
					const items = section.Items;
					items.splice(y, 1, item);
					section.Items = items;
					return;
				}
			}
		}
	},
	[UPDATE_PRODUCT_SHOPPING_LIST_SECTIONS]: (
		state: IRootState,
		sections: ProductShoppingListSection[]
	) => {
		sections.forEach((s) => {
			const sectionIndex: number =
				state.productShoppingListSections.findIndex((sec) => {
					return s.Heading === sec.Heading;
				});
			if (sectionIndex < 0) {
				state.productShoppingListSections.push(s);
			} else {
				state.productShoppingListSections.splice(sectionIndex, 1, s);
			}
		});
	},
	[OPEN_MXR_SWAP_MODAL]: (state: IRootState, { item, swapSection }) => {
		state.selectedSwap = item;
		state.selectedSwapSection = swapSection;
		state.swapSlot = true;
	},
	[CLOSE_SWAP_MODAL]: (state: IRootState) => {
		state.swapOptions = [];
		state.swapSlot = false;
	},
	[UPDATE_SWAP_OPTIONS]: (
		state: IRootState,
		payload: ProductShoppingListItem
	) => {
		if (!state.mxrRetailerLocations.length) {
			state.swapOptions = [];
			return;
		}
		const options: ProductShoppingListItem[] = state.mxrRetailerLocations[
			state.currentMxrRetailerIndex
		].Offers.map((o: RetailerOffer) => {
			if (o.ItemId === payload.ItemId && o.Id !== payload.Id) {
				return ProductShoppingListItem.createFromRetailerOffer(
					o,
					payload.Name
				);
			}
			return new ProductShoppingListItem(
				'',
				'',
				'',
				'',
				'',
				false,
				false,
				'',
				'',
				''
			);
		}).filter((p) => p.Id.length);
		state.swapOptions = options;
	},
	[UPDATE_VISIBLE_INGREDIENTS]: (state: IRootState, payload) => {
		state.selectedSwap = payload;
		state.productShoppingListSections[
			state.selectedSwapSection
		].Items.forEach((sec, i) => {
			if (sec.ItemId === state.selectedSwap.ItemId) {
				state.productShoppingListSections[
					state.selectedSwapSection
				].Items.splice(i, 1, state.selectedSwap);
			}
			return state.productShoppingListSections;
		});
	},
	[TOGGLE_RETAILER_LOCATIONS_MODAL]: (state: IRootState) => {
		state.retailerLocationsModalOpen = !state.retailerLocationsModalOpen;
	},
	[SET_BETA](state: IRootState, isBeta: boolean) {
		state.isBeta = isBeta;
	},
	[SET_TAKEOVER](state: IRootState, takeover: Takeover) {
		// if (state.mxrRetailerLocations.length === 0) {
		//   // state.takeover.Id = '85eaf56b-45c3-433c-8248-9b0ad04369f3';
		//   state.takeover = takeover;
		// }
		state.takeover = takeover;
	},
	[SET_IS_DEFAULT]: (state: IRootState, isDefault: boolean) => {
		state.isDefault = isDefault;
	},
	[SET_KROGER_AUTH_CONFIG]: (
		state: IRootState,
		authConfig: AuthOnBehalfOfConfig
	) => {
		state.krogerAuthConfig = authConfig;
	},
	[SET_GO_TO_CART_OPEN]: (state: IRootState, open: boolean) => {
		state.goToCartOpen = open;
	},
	[SET_CAROUSEL_PROMOTIONS]: (
		state: IRootState,
		carousels: PromotionCarouselItem[]
	) => {
		state.config.promotions = carousels.map((carousel, c) => {
			if (carousel.promotions.length) {
				state.visiblePromotionIds.splice(
					c,
					1,
					carousel.promotions[0].id.toString()
				);
				state.visiblePromotionUniqueIds.splice(
					c,
					1,
					carousel.promotions[0].id.toString()
				);
			}
			return carousel;
		});
	},
	[SET_MXR_CONFIG]: (state: IRootState, config: IMxrConfig) => {
		state.config = config;
		if (state.config.cards.length === 0) {
			return;
		}
		if (isFirstConfigSet) {
			return;
		}
		isFirstConfigSet = true;
		state.activeCardIndex = !state.config.randomizeCardsDisplayed
			? 0
			: Math.floor(Math.random() * Math.floor(state.config.cards.length));
	},
	[SET_ACTIVE_CARD]: (state: IRootState, index: number) => {
		state.activeCardIndex = index;
	},
	[SET_SELECTED_SWAP]: (state: IRootState, payload) => {
		state.selectedSwap = payload;
	},
	[SET_CHECKOUT_TOTAL]: (state: IRootState, payload: number) => {
		state.checkoutTotal = payload;
	},
	[SET_PREVIOUS_PROMOTION]: (state: IRootState, payload) => {
		state.previousPromotion = payload;
	},
	[SET_OFFERS_COUNT_IN_CART]: (state: IRootState, payload: number) => {
		state.offersCountInCart = payload;
	}
};
